import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, catchError, combineLatest, map, of, startWith, switchMap } from 'rxjs';
import { DivineServiceHomePageComponent } from '@features/divine-services/pages/divine-service-home-page/divine-service-home-page.component';
import { UnitService } from '../services/unit.service';

@Component({
  selector: 'app-unit-latest-divine-service-page',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatProgressSpinnerModule, DivineServiceHomePageComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="development-page">
      <span class="development-ribbon">En desarrollo</span>
    @if (vm$ | async; as vm) {
      @if (vm.loading) {
        <div class="state" role="status"><mat-spinner diameter="32"></mat-spinner><p>Cargando último servicio divino…</p></div>
      } @else if (vm.error) {
        <div class="state" role="alert"><p>No se pudo cargar el último servicio divino.</p><button mat-stroked-button (click)="retry()">Reintentar</button></div>
      } @else if (vm.serviceId) {
        <app-divine-service-home-page [serviceId]="vm.serviceId" [updateUnitBreadcrumb]="false"></app-divine-service-home-page>
      } @else {
        <div class="state"><h1>Último SD</h1><p>No hay servicios divinos con fecha de hoy o anterior en esta unidad.</p></div>
      }
    }
    </div>
  `,
  styles: [`
    :host { display: block; }
    .development-page { position: relative; isolation: isolate; overflow: hidden; padding-top: 48px; }
    .development-ribbon { position: absolute; z-index: 200; pointer-events: none; top: 22px; right: -36px; width: 140px; padding: 4px 0; background: #653594; color: #fff; transform: rotate(45deg); text-align: center; font-size: 11px; font-weight: 600; line-height: 16px; white-space: nowrap; }
    .state { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 32px 16px; color: #6d89a2; text-align: center; }
    h1 { margin: 0; color: #214f79; font-size: 20px; font-weight: 500; }
  `],
})
export class UnitLatestDivineServicePageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly units = inject(UnitService);
  private readonly reload$ = new Subject<void>();

  readonly vm$ = combineLatest([
    this.route.parent!.parent!.paramMap,
    this.reload$.pipe(startWith(void 0)),
  ]).pipe(switchMap(([params]) => this.units.getLatestDivineServices(params.get('unit-id')!, 1).pipe(
    map(services => ({ serviceId: services[0]?.id ?? null, loading: false, error: false })),
    startWith({ serviceId: null, loading: true, error: false }),
    catchError(() => of({ serviceId: null, loading: false, error: true })),
  )));

  retry(): void { this.reload$.next(); }
}
